import {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query"
import { UserbotTracking as T } from "../interfaces/tracking"
import { userbotApi } from "./userbotApi"
import {
  scheduleRunsRequest,
  trackingListRequest,
  trackingResourceRequest,
} from "./trackingQuery"

// Validate before HTTP, rather than retrying an unscoped or malformed request.
const safeQuery =
  <Result, Arg>(request: (arg: Arg) => FetchArgs) =>
  async (
    arg: Arg,
    _api: BaseQueryApi,
    _options: unknown,
    baseQuery: (args: FetchArgs) => ReturnType<BaseQueryFn>,
  ) => {
    let args: FetchArgs
    try {
      args = request(arg)
    } catch (error) {
      return {
        error: {
          status: "CUSTOM_ERROR",
          error: (error as Error).message,
        } as FetchBaseQueryError,
      }
    }
    const result = await baseQuery(args)
    return result.error
      ? { error: result.error as FetchBaseQueryError }
      : { data: result.data as Result }
  }

export const trackingApi = userbotApi.injectEndpoints({
  endpoints: (build) => ({
    scheduleRuns: build.query<T.Page<T.ScheduleRun>, T.ScheduleRunsQuery>({
      queryFn: safeQuery(scheduleRunsRequest),
      providesTags: (_res, _err, { id }) => [{ type: "ScheduleRuns", id }],
    }),
    joinRequests: build.query<T.Page<T.JoinRequest>, T.JoinRequestsQuery>({
      queryFn: safeQuery((args) => trackingListRequest("requests", args)),
      providesTags: (_res, _err, args) => [
        { type: "JoinRequests", id: args.accountId || "ALL" },
      ],
    }),
    joinRequest: build.query<T.JoinRequest, T.AccountResource>({
      queryFn: safeQuery((args) =>
        trackingResourceRequest("join-requests", args, true),
      ),
      providesTags: (_res, _err, args) => [
        { type: "JoinRequest", id: `${args.accountId}:${args.id}` },
      ],
    }),
    cancelJoinRequest: build.mutation<T.JoinRequest, T.AccountResource>({
      queryFn: safeQuery((args) =>
        trackingResourceRequest("join-requests", args, true, true),
      ),
      invalidatesTags: (_res, _err, args) => [
        { type: "JoinRequests", id: args.accountId },
        { type: "JoinRequests", id: "ALL" },
        { type: "JoinRequest", id: `${args.accountId}:${args.id}` },
      ],
    }),
    joinRecovery: build.query<T.Page<T.JoinRecovery>, T.JoinRecoveryQuery>({
      queryFn: safeQuery((args) => trackingListRequest("recovery", args)),
      providesTags: ["JoinRecovery"],
    }),
    joinRecoveryDetail: build.query<T.JoinRecovery, { id: string }>({
      queryFn: safeQuery((args) =>
        trackingResourceRequest("join-requests/recovery", args, false),
      ),
      providesTags: ["JoinRecovery"],
    }),
    dialogs: build.query<T.Page<T.Dialog>, T.DialogsQuery>({
      queryFn: safeQuery((args) => trackingListRequest("dialogs", args)),
      providesTags: ["Dialogs"],
    }),
    dialog: build.query<T.Dialog, T.AccountResource>({
      queryFn: safeQuery((args) =>
        trackingResourceRequest("dialogs", args, true),
      ),
      providesTags: ["Dialogs"],
    }),
    dialogPasses: build.query<T.Page<T.DialogPass>, T.DialogPassesQuery>({
      queryFn: safeQuery((args) => trackingListRequest("passes", args)),
      providesTags: ["DialogPasses"],
    }),
    membershipClarifications: build.query<
      T.Page<T.MembershipClarification>,
      T.ClarificationsQuery
    >({
      queryFn: safeQuery((args) => trackingListRequest("clarifications", args)),
      providesTags: ["MembershipClarifications"],
    }),
  }),
})
export const {
  useScheduleRunsQuery,
  useJoinRequestsQuery,
  useJoinRequestQuery,
  useCancelJoinRequestMutation,
  useJoinRecoveryQuery,
  useJoinRecoveryDetailQuery,
  useDialogsQuery,
  useDialogQuery,
  useDialogPassesQuery,
  useMembershipClarificationsQuery,
} = trackingApi
